import moment from 'moment';

// order matters: the index is used as severity rank (lower index = more severe)
export const SEVERITY_ORDER = ['critical', 'moderate', 'minor'];

export const SEVERITY_COLORS = {
   critical: '#ef4444',
   moderate: '#f59e0b',
   minor: '#3b82f6',
};

export const DEFAULT_SEVERITY_COLOR = '#6b7280';

// the alerting API types several alert props as `value | Array<value>`, so normalize them for rendering
export function asText(value) {
   return Array.isArray(value) ? value.join(' | ') : value;
}

export function severityRank(severity) {
   const ranks = []
      .concat(severity ?? [])
      .map((s) => SEVERITY_ORDER.indexOf(s))
      .filter((rank) => rank >= 0);
   return ranks.length > 0 ? Math.min(...ranks) : SEVERITY_ORDER.length;
}

export function getSeverityColor(severity) {
   return SEVERITY_COLORS[SEVERITY_ORDER[severityRank(severity)]] ?? DEFAULT_SEVERITY_COLOR;
}

/**
 * The API transports the alert name but not its label, while `type.label` only names the coarser
 * category. Kept in sync with `ALERTS` in aux-auxiliary-functions/src/services/AlertService/AlertsTypes.ts.
 *
 * The keys are the `ALERT_NAME` *values* that the alerting writes to `notification.name`, not the enum
 * identifiers: three of them were renamed for readability while their value deliberately kept the older
 * wording, so `CONTINUOUS_RESTARTS_POOR_PERFORMANCE`, `STR_SUBOPTIMAL` and `STR_POOR_PERFORMANCE` carry
 * the labels of their renamed identifier here.
 */
export const ALERT_LABELS = {
   STREAM_OFFLINE: 'Stream offline',
   CONTINUOUS_RESTARTS: 'Continuous restarts',
   CONTINUOUS_RESTARTS_POOR_PERFORMANCE: 'Continuous restarts with critical stream time ratio',
   INACTIVE_INGESTS: 'Inactively running ingests',
   INACTIVE_AND_ACTIVE_INGESTS: 'Mixed inactively/actively running ingests',
   STR_SUBOPTIMAL: 'Moderate stream time ratio performance',
   STR_POOR_PERFORMANCE: 'Critical stream time ratio performance',
   MFD_PERSISTENT_DELAY: 'Persistently high frame delay',
   MFD_PERSISTENT_DELAY_ULL: 'Persistently elevated frame delay (ultra low latency)',
   MFD_STALLS: 'Recurring ingest stalls',
   DUPLICATED_INGEST: 'Duplicated ingests',
   DUPLICATED_INGEST_DIFFERENT_INFRASTRUCTURE: 'Duplicated ingests on different infrastructures',
   DUPLICATED_INGEST_DIFFERENT_LOCATIONS: 'Duplicated ingests at different locations',
   DUPLICATED_INGEST_DIFFERENT_IPS: 'Duplicated ingests with different IPs',
};

// the coarser category an alert belongs to, delivered by the API
export function getAlertTypeLabel(alert) {
   return alert.type?.label ?? alert.type?.name ?? '';
}

// falls back to the alert type as long as an unknown alert name has no label here yet
export function getAlertLabel(alert) {
   const labels = []
      .concat(alert.name ?? [])
      .map((name) => ALERT_LABELS[name])
      .filter(Boolean);
   return labels.length > 0 ? labels.join(' | ') : getAlertTypeLabel(alert) || 'Unknown alert';
}

/**
 * Stream names are prefixed with the hash of their organization (e.g. 'fNq3T-08R8b'). The API resolves
 * the organization of a stream the same way, see `parseStreamName` in the analytics backend.
 */
export function getOrgaHashOfStream(stream) {
   return stream ? stream.split('-')[0] : '';
}

// alerts without a first detection (e.g. offline streams) are sorted last
function getFirstDetectionInMs(alert) {
   return alert.firstDetection ? moment.utc(alert.firstDetection).valueOf() : Number.MAX_SAFE_INTEGER;
}

/**
 * A request scoped to one organization answers with `data.notifications`, whereas an admin request
 * without an organization filter answers with `data.organizations[<orgaHash>].notifications`.
 */
function getNotificationBuckets(data) {
   if (Array.isArray(data?.notifications)) return data.notifications;
   return Object.values(data?.organizations ?? {}).flatMap((orga) => orga.notifications ?? []);
}

export function selectStreamAlerts(responseBody, stream) {
   if (!stream) return [];
   const bucket = getNotificationBuckets(responseBody?.data).find((entry) => entry.streamname === stream);
   return [...(bucket?.alerts ?? [])].sort(
      (a, b) =>
         severityRank(a.severity) - severityRank(b.severity) || getFirstDetectionInMs(a) - getFirstDetectionInMs(b),
   );
}

/**
 * Duration of an ongoing alert in a compact readable form ('2 min', '1h 40m', '23h'), measured against
 * the snapshot time of the alerting data (which lags a few minutes behind) instead of the current time.
 */
export function getAlertDuration(firstDetection, snapshotTime) {
   if (!firstDetection) return '';
   const from = moment.utc(firstDetection);
   const to = snapshotTime ? moment.utc(snapshotTime) : moment.utc();
   const totalMinutes = Math.max(to.diff(from, 'minutes'), 0);

   if (totalMinutes < 1) return '< 1 min';

   const hours = Math.floor(totalMinutes / 60);
   const minutes = totalMinutes % 60;

   if (hours === 0) return `${minutes} min`;
   return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
}
